import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import { Newspaper, Users, UserPlus, UserMinus } from "@phosphor-icons/react";
import { toast } from "sonner";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

/**
 * Facebook-style hover popover for a username.
 * Fetches a lightweight public profile snapshot on hover and shows a card with
 * avatar, display name, username, verified/admin badge, bio, follower counts.
 * Wrap around a username link — the popover appears on hover/focus.
 */
export default function UserHoverCard({ userId, username, children, className = "" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const scheduleOpen = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(true), 300);
  };
  const scheduleClose = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(false), 200);
  };

  useEffect(() => {
    if (!open || data || busy || !userId) return;
    setBusy(true);
    api.get(`/users/${userId}/profile`).then((r) => setData(r.data)).catch(() => {}).finally(() => setBusy(false));
  }, [open, userId, data, busy]);

  const toggleFollow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!data || !userId || followBusy) return;
    setFollowBusy(true);
    try {
      const endpoint = data.is_following ? "unfollow" : "follow";
      const { data: res } = await api.post(`/users/${userId}/${endpoint}`);
      setData((d) => ({
        ...d,
        is_following: res.is_following,
        followers_count: (d.followers_count || 0) + (res.is_following ? 1 : -1),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setFollowBusy(false); }
  };

  const u = data?.user;
  const src = resolveImg(u?.avatar_url);

  return (
    <span
      ref={wrapRef}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={scheduleOpen}
      onBlur={scheduleClose}
      className={`relative inline-block ${className}`}
    >
      {children}
      {open && (
        <span
          data-testid={`hover-card-${username || userId}`}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          className="absolute z-50 left-0 top-full mt-1 w-72 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden"
          style={{ cursor: "auto" }}
        >
          {!data && (
            <span className="block px-4 py-6 text-sm text-slate-500 text-center">Loading…</span>
          )}
          {u && (
            <span className="block p-4">
              <span className="flex items-start gap-3">
                <Link to={`/u/${u.id}`} className="shrink-0">
                  {src ? (
                    <img src={src} alt={u.username} className="h-14 w-14 rounded-full object-cover" />
                  ) : (
                    <span className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xl font-black">
                      {(u.username || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </Link>
                <span className="flex-1 min-w-0">
                  <Link to={`/u/${u.id}`} className="font-black text-slate-900 dark:text-white text-lg inline-flex items-center gap-1 hover:underline">
                    <span className="truncate">{u.full_name || u.username}</span>
                    <UserBadge user={u} size={14} />
                  </Link>
                  <Link to={`/u/${u.id}`} className="block text-xs text-slate-500 hover:underline">@{u.username}</Link>
                  {u.membership && u.membership !== "Free" && (
                    <span className="mt-1 inline-block text-[10px] font-bold uppercase tracking-widest text-yellow-600">{u.membership}</span>
                  )}
                </span>
              </span>
              {u.bio && (
                <span className="block mt-3 text-xs text-slate-600 dark:text-slate-300 line-clamp-3 whitespace-pre-wrap break-words">{u.bio}</span>
              )}
              <span className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Stat icon={Newspaper} label="Posts" value={data.posts_count} />
                <Stat icon={Users} label="Followers" value={data.followers_count} />
                <Stat icon={Users} label="Following" value={data.following_count} />
              </span>
              {!data.is_self && (
                <span className="mt-3 flex gap-2">
                  <Link
                    to={`/u/${u.id}`}
                    data-testid={`hover-view-profile-${username || userId}`}
                    className="flex-1 text-center text-xs font-bold rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 px-3 py-1.5 transition-colors duration-200"
                  >
                    View profile
                  </Link>
                  <button
                    onClick={toggleFollow}
                    disabled={followBusy}
                    data-testid={`hover-follow-${username || userId}`}
                    className={`flex-1 text-xs font-bold rounded-full px-3 py-1.5 inline-flex items-center justify-center gap-1 transition-colors duration-200 ${data.is_following ? "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100 hover:bg-red-500 hover:text-white" : "bg-blue-700 hover:bg-blue-600 text-white"}`}
                  >
                    {data.is_following ? <><UserMinus size={12} weight="bold" /> Following</> : <><UserPlus size={12} weight="bold" /> Follow</>}
                  </button>
                </span>
              )}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <span className="rounded-xl bg-slate-50 dark:bg-slate-800 py-1.5">
      <Icon size={12} weight="bold" className="mx-auto text-slate-500 block" />
      <span className="block text-sm font-black text-slate-900 dark:text-white leading-tight">{value ?? 0}</span>
      <span className="block text-[9px] uppercase tracking-widest text-slate-500">{label}</span>
    </span>
  );
}
